import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  CreditCard,
  Settings,
  Save,
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
  DollarSign,
  Package,
  Webhook,
  Eye,
  EyeOff,
  Zap,
  ShieldCheck,
  TrendingUp,
  Pencil,
  Lock,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function StripeManager() {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Stripe data
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [balance, setBalance] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    test_secret_key: '',
    test_publishable_key: '',
    live_secret_key: '',
    live_publishable_key: '',
    webhook_secret: '',
    mode: 'test',
    enabled: true
  });
  
  // Show/hide keys
  const [showKeys, setShowKeys] = useState({
    test_secret: false,
    live_secret: false,
    webhook: false
  });
  
  // Edit mode state
  const [isEditingStripe, setIsEditingStripe] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/stripe/settings`);
      setSettings(response.data);
      const loadedFormData = {
        test_secret_key: '',
        test_publishable_key: response.data.test_publishable_key || '',
        live_secret_key: '',
        live_publishable_key: response.data.live_publishable_key || '',
        webhook_secret: '',
        mode: response.data.mode || 'test',
        enabled: response.data.enabled !== false
      };
      setFormData(loadedFormData);
      setOriginalFormData(loadedFormData);
    } catch (err) {
      console.error('Failed to fetch Stripe settings:', err);
      toast.error('Failed to load Stripe settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelStripeEdit = () => {
    if (originalFormData) {
      setFormData(originalFormData);
    }
    setIsEditingStripe(false);
  };

  const handleTestConnection = async (mode = null) => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/admin/stripe/test-connection`, null, {
        params: mode ? { mode } : {}
      });
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Stripe connection successful!');
        // Fetch Stripe data after successful connection
        fetchStripeData();
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error('Connection test failed');
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Connection failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const fetchStripeData = async () => {
    try {
      const [productsRes, pricesRes, customersRes, paymentsRes, subsRes, balanceRes, webhooksRes] = await Promise.allSettled([
        axios.get(`${API}/admin/stripe/products`),
        axios.get(`${API}/admin/stripe/prices`),
        axios.get(`${API}/admin/stripe/customers`),
        axios.get(`${API}/admin/stripe/payments`),
        axios.get(`${API}/admin/stripe/subscriptions`),
        axios.get(`${API}/admin/stripe/balance`),
        axios.get(`${API}/admin/stripe/webhooks`)
      ]);
      
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data);
      if (pricesRes.status === 'fulfilled') setPrices(pricesRes.value.data);
      if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.data);
      if (paymentsRes.status === 'fulfilled') setPayments(paymentsRes.value.data);
      if (subsRes.status === 'fulfilled') setSubscriptions(subsRes.value.data);
      if (balanceRes.status === 'fulfilled') setBalance(balanceRes.value.data);
      if (webhooksRes.status === 'fulfilled') setWebhooks(webhooksRes.value.data);
    } catch (err) {
      console.error('Failed to fetch Stripe data:', err);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/stripe/settings`, formData);
      toast.success('Stripe settings saved');
      setOriginalFormData(formData);
      setIsEditingStripe(false); // Lock after save
      setSettingsOpen(false);
      fetchSettings();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatAmount = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#6D4AFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <CreditCard className="w-7 h-7 text-[#635BFF]" />
            Stripe Integration
          </h2>
          <p className="text-gray-400 mt-1">Configure payment processing and view transactions</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleTestConnection()}
            disabled={testing}
            className="border-[#2d2d44] text-gray-300 hover:bg-[#2d2d44]"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Test Connection
          </Button>
          <Button
            onClick={() => setSettingsOpen(true)}
            className="bg-[#635BFF] hover:bg-[#5347DB]"
          >
            <Settings className="w-4 h-4 mr-2" />
            API Settings
          </Button>
        </div>
      </div>

      {/* Connection Status */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${testResult?.success ? 'bg-green-500/20' : settings?.env_key_configured ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                {testResult?.success ? (
                  <CheckCircle className="w-6 h-6 text-green-400" />
                ) : settings?.env_key_configured ? (
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-400" />
                )}
              </div>
              <div>
                <p className="text-white font-medium">
                  {testResult?.success ? 'Connected to Stripe' : settings?.env_key_configured ? 'API Key Configured' : 'Not Configured'}
                </p>
                <p className="text-sm text-gray-400">
                  {testResult?.success ? (
                    <>Account: {testResult.account_name || testResult.account_id}</>
                  ) : settings?.env_key_configured ? (
                    <>Environment: {settings.env_key_masked} ({settings.env_key_mode})</>
                  ) : (
                    'No Stripe API key found'
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge className={settings?.mode === 'live' ? 'bg-green-500' : 'bg-yellow-500'}>
                {settings?.mode === 'live' ? 'Live Mode' : 'Test Mode'}
              </Badge>
              {testResult?.success && (
                <div className="text-right">
                  <p className="text-xs text-gray-500">Charges: {testResult.has_charges_enabled ? '✓' : '✗'}</p>
                  <p className="text-xs text-gray-500">Payouts: {testResult.has_payouts_enabled ? '✓' : '✗'}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Balance Card */}
      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-500/20">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">
                    {balance.available?.[0] ? formatAmount(balance.available[0].amount, balance.available[0].currency) : '$0.00'}
                  </p>
                  <p className="text-sm text-gray-400">Available Balance</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-yellow-500/20">
                  <TrendingUp className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">
                    {balance.pending?.[0] ? formatAmount(balance.pending[0].amount, balance.pending[0].currency) : '$0.00'}
                  </p>
                  <p className="text-sm text-gray-400">Pending Balance</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#252540]">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#635BFF]">Overview</TabsTrigger>
          <TabsTrigger value="products" className="data-[state=active]:bg-[#635BFF]">Products</TabsTrigger>
          <TabsTrigger value="customers" className="data-[state=active]:bg-[#635BFF]">Customers</TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-[#635BFF]">Payments</TabsTrigger>
          <TabsTrigger value="subscriptions" className="data-[state=active]:bg-[#635BFF]">Subscriptions</TabsTrigger>
          <TabsTrigger value="webhooks" className="data-[state=active]:bg-[#635BFF]">Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-blue-500/20">
                    <Package className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{products.length}</p>
                    <p className="text-sm text-gray-400">Products</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-purple-500/20">
                    <Users className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{customers.length}</p>
                    <p className="text-sm text-gray-400">Customers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-green-500/20">
                    <CreditCard className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{payments.length}</p>
                    <p className="text-sm text-gray-400">Payments</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-amber-500/20">
                    <ShieldCheck className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{subscriptions.length}</p>
                    <p className="text-sm text-gray-400">Subscriptions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="products">
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Package className="w-5 h-5" />
                Products ({products.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2d2d44]">
                      <TableHead className="text-gray-400">Name</TableHead>
                      <TableHead className="text-gray-400">ID</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id} className="border-[#2d2d44]">
                        <TableCell className="text-white font-medium">{product.name}</TableCell>
                        <TableCell className="text-gray-400 font-mono text-xs">{product.id}</TableCell>
                        <TableCell>
                          <Badge className={product.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {product.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-400">{formatDate(product.created)}</TableCell>
                      </TableRow>
                    ))}
                    {products.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                          No products found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customers">
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5" />
                Customers ({customers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2d2d44]">
                      <TableHead className="text-gray-400">Email</TableHead>
                      <TableHead className="text-gray-400">Name</TableHead>
                      <TableHead className="text-gray-400">Balance</TableHead>
                      <TableHead className="text-gray-400">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.id} className="border-[#2d2d44]">
                        <TableCell className="text-white">{customer.email || '-'}</TableCell>
                        <TableCell className="text-gray-400">{customer.name || '-'}</TableCell>
                        <TableCell className="text-gray-400">
                          {customer.balance ? formatAmount(customer.balance, customer.currency || 'usd') : '$0.00'}
                        </TableCell>
                        <TableCell className="text-gray-400">{formatDate(customer.created)}</TableCell>
                      </TableRow>
                    ))}
                    {customers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                          No customers found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Recent Payments ({payments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2d2d44]">
                      <TableHead className="text-gray-400">Amount</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Description</TableHead>
                      <TableHead className="text-gray-400">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id} className="border-[#2d2d44]">
                        <TableCell className="text-white font-medium">
                          {formatAmount(payment.amount, payment.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            payment.status === 'succeeded' ? 'bg-green-500/20 text-green-400' :
                            payment.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }>
                            {payment.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-400">{payment.description || '-'}</TableCell>
                        <TableCell className="text-gray-400">{formatDate(payment.created)}</TableCell>
                      </TableRow>
                    ))}
                    {payments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                          No payments found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscriptions">
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                Subscriptions ({subscriptions.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2d2d44]">
                      <TableHead className="text-gray-400">ID</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Period End</TableHead>
                      <TableHead className="text-gray-400">Cancel at End</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((sub) => (
                      <TableRow key={sub.id} className="border-[#2d2d44]">
                        <TableCell className="text-white font-mono text-xs">{sub.id}</TableCell>
                        <TableCell>
                          <Badge className={
                            sub.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            sub.status === 'trialing' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-red-500/20 text-red-400'
                          }>
                            {sub.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-400">{formatDate(sub.current_period_end)}</TableCell>
                        <TableCell className="text-gray-400">{sub.cancel_at_period_end ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                    ))}
                    {subscriptions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                          No subscriptions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks">
          <Card className="bg-[#1a1a2e] border-[#2d2d44]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Webhook className="w-5 h-5" />
                Webhook Endpoints ({webhooks.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2d2d44]">
                      <TableHead className="text-gray-400">URL</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400">Events</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {webhooks.map((webhook) => (
                      <TableRow key={webhook.id} className="border-[#2d2d44]">
                        <TableCell className="text-white font-mono text-xs">{webhook.url}</TableCell>
                        <TableCell>
                          <Badge className={webhook.status === 'enabled' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {webhook.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-400">{webhook.enabled_events?.length || 0} events</TableCell>
                      </TableRow>
                    ))}
                    {webhooks.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-gray-500 py-8">
                          No webhooks configured
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={(open) => {
        setSettingsOpen(open);
        if (!open) setIsEditingStripe(false);
      }}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-white flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Stripe API Configuration
              </DialogTitle>
              {!isEditingStripe ? (
                <Button
                  onClick={() => setIsEditingStripe(true)}
                  variant="outline"
                  size="sm"
                  className="border-[#635BFF] text-[#635BFF] hover:bg-[#635BFF] hover:text-white"
                  data-testid="stripe-edit-btn"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              ) : (
                <Button
                  onClick={handleCancelStripeEdit}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                  data-testid="stripe-cancel-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              )}
            </div>
            {!isEditingStripe && (
              <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Settings are locked. Click Edit to make changes.
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Mode Toggle */}
            <div className={`flex items-center justify-between p-4 bg-[#252540] rounded-lg ${!isEditingStripe ? 'opacity-60' : ''}`}>
              <div>
                <p className="text-white font-medium">Live Mode</p>
                <p className="text-xs text-gray-400">Switch between test and live mode</p>
              </div>
              <Switch
                checked={formData.mode === 'live'}
                onCheckedChange={(checked) => setFormData({...formData, mode: checked ? 'live' : 'test'})}
                disabled={!isEditingStripe}
              />
            </div>

            {/* Test Keys */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                <Badge className="bg-yellow-500/20 text-yellow-400">Test</Badge>
                Test Mode Keys
              </h4>
              
              <div className="space-y-2">
                <Label className="text-gray-400">Secret Key (sk_test_...)</Label>
                <div className="relative">
                  <Input
                    type={showKeys.test_secret ? 'text' : 'password'}
                    placeholder={settings?.test_secret_key_masked || 'sk_test_...'}
                    value={formData.test_secret_key}
                    onChange={(e) => setFormData({...formData, test_secret_key: e.target.value})}
                    className="bg-[#252540] border-[#2d2d44] text-white pr-10 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={!isEditingStripe}
                    data-testid="stripe-test-secret-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-500"
                    onClick={() => setShowKeys({...showKeys, test_secret: !showKeys.test_secret})}
                  >
                    {showKeys.test_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-400">Publishable Key (pk_test_...)</Label>
                <Input
                  placeholder="pk_test_..."
                  value={formData.test_publishable_key}
                  onChange={(e) => setFormData({...formData, test_publishable_key: e.target.value})}
                  className="bg-[#252540] border-[#2d2d44] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={!isEditingStripe}
                  data-testid="stripe-test-pub-input"
                />
              </div>
            </div>

            {/* Live Keys */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                <Badge className="bg-green-500/20 text-green-400">Live</Badge>
                Live Mode Keys
              </h4>
              
              <div className="space-y-2">
                <Label className="text-gray-400">Secret Key (sk_live_...)</Label>
                <div className="relative">
                  <Input
                    type={showKeys.live_secret ? 'text' : 'password'}
                    placeholder={settings?.live_secret_key_masked || 'sk_live_...'}
                    value={formData.live_secret_key}
                    onChange={(e) => setFormData({...formData, live_secret_key: e.target.value})}
                    className="bg-[#252540] border-[#2d2d44] text-white pr-10 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={!isEditingStripe}
                    data-testid="stripe-live-secret-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-500"
                    onClick={() => setShowKeys({...showKeys, live_secret: !showKeys.live_secret})}
                  >
                    {showKeys.live_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-400">Publishable Key (pk_live_...)</Label>
                <Input
                  placeholder="pk_live_..."
                  value={formData.live_publishable_key}
                  onChange={(e) => setFormData({...formData, live_publishable_key: e.target.value})}
                  className="bg-[#252540] border-[#2d2d44] text-white disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={!isEditingStripe}
                  data-testid="stripe-live-pub-input"
                />
              </div>
            </div>

            {/* Webhook Secret */}
            <div className="space-y-2">
              <Label className="text-gray-400">Webhook Secret (whsec_...)</Label>
              <div className="relative">
                <Input
                  type={showKeys.webhook ? 'text' : 'password'}
                  placeholder={settings?.webhook_secret_masked || 'whsec_...'}
                  value={formData.webhook_secret}
                  onChange={(e) => setFormData({...formData, webhook_secret: e.target.value})}
                  className="bg-[#252540] border-[#2d2d44] text-white pr-10 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={!isEditingStripe}
                  data-testid="stripe-webhook-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-500"
                  onClick={() => setShowKeys({...showKeys, webhook: !showKeys.webhook})}
                >
                  {showKeys.webhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Environment Key Info */}
            {settings?.env_key_configured && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400">
                  <strong>Environment Key:</strong> {settings.env_key_masked} ({settings.env_key_mode})
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  This key is used as fallback when no database keys are configured.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => {setSettingsOpen(false); setIsEditingStripe(false);}} className="text-gray-400">
              Cancel
            </Button>
            {isEditingStripe && (
              <Button onClick={handleSaveSettings} disabled={saving} className="bg-[#635BFF] hover:bg-[#5347DB]" data-testid="stripe-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
